import { ReactNode } from 'react';
import { EmulatorType, PlanFamily } from '@localstack/types';
import { HobbyLegalFineprint, StartPreviewLegalFineprint } from '@localstack/ui';
import { AZURE_KEY_FEATURES } from '@localstack/constants';

export type PlanFamilyDescription = {
  name: string;
  description: string;
  features: string[];
  emulator?: 'aws' | 'snowflake';
  buttonText?: string;
  showExternalPurchaseLink?: boolean;
  tosFineprint?: ReactNode;
};

type KnownAwsPlanFamilies = Extract<
  PlanFamily,
  | PlanFamily.TRIAL_PLANS
  | PlanFamily.HOBBY_PLANS
  | PlanFamily.BASE_PLANS
  | PlanFamily.STUDENT
  | PlanFamily.ULTIMATE_PLANS
  | PlanFamily.ENTERPRISE_PLANS
  | PlanFamily.FREEMIUM_PLANS
  | PlanFamily.PRO_PLANS
  | PlanFamily.TEAM_PLANS
  | PlanFamily.LEGACY_PLANS
  | PlanFamily.COMMUNITY
>;

type KnownSnowflakePlanFamilies = Extract<
  PlanFamily,
  PlanFamily.TRIAL_PLANS | PlanFamily.BASE_PLANS | PlanFamily.ENTERPRISE_PLANS
>;

/** Azure is preview-only: the backend ships a single plan in the preview family. */
type KnownAzurePlanFamilies = Extract<PlanFamily, PlanFamily.PREVIEW_PLANS>;

const NUMBER_OF_SERVICES_IN_PRO = '100+';

const AWS_PLAN_FAMILY_MAP: Record<KnownAwsPlanFamilies, PlanFamilyDescription> = {
  [PlanFamily.HOBBY_PLANS]: {
    name: 'Hobby',
    description: `For individuals (non commercial)`,
    features: [`${NUMBER_OF_SERVICES_IN_PRO} AWS services`, 'CI starter tier¹', 'IAM security enforcement'],
  },
  [PlanFamily.TRIAL_PLANS]: {
    name: 'Trial',
    description: 'Try out the power of LocalStack for AWS',
    features: ['110+ AWS Services', 'IAM Policy Stream', 'AWS Replicator'],
    buttonText: 'Start trial',
  },
  [PlanFamily.BASE_PLANS]: {
    name: 'Base',
    description: 'For teams building simple applications',
    features: ['55+ emulated services', 'Run tests in CI', 'State persistence tooling', 'Application previews'],
  },
  [PlanFamily.ULTIMATE_PLANS]: {
    name: 'Ultimate',
    description: 'For teams building complex applications',
    features: ['110+ emulated services', 'Run tests in CI', 'IAM policy automation', 'AWS resource replication'],
    showExternalPurchaseLink: true,
  },
  [PlanFamily.STUDENT]: {
    name: 'Student',
    description: 'For individuals enrolled in the Github Student program',
    features: ['110+ AWS Services', 'IAM Policy Stream', 'AWS Replicator'],
  },
  [PlanFamily.ENTERPRISE_PLANS]: {
    name: 'Enterprise',
    description: 'Built to integrate into your infrastructure',
    features: [
      'Deployment via shared infrastructure',
      'Run tests in Kubernetes',
      'Airgapped image and deployment options',
      'White glove service',
    ],
    buttonText: 'Contact sales',
  },
  [PlanFamily.FREEMIUM_PLANS]: {
    name: 'Hobby',
    description: 'Great for developing cloud apps',
    features: [
      'Non-commercial, single-user plan',
      'Debug & inspect locally',
      'Run tests in CI',
      '30+ emulated services',
    ],
    buttonText: 'Continue Free',
    tosFineprint: <HobbyLegalFineprint />,
  },
  [PlanFamily.PRO_PLANS]: {
    name: 'Starter',
    description: 'For individuals',
    features: [
      `${NUMBER_OF_SERVICES_IN_PRO} AWS services`,
      'CI Starter Tier¹',
      'IAM Security Enforcement',
      'State Persistence',
      'Ephemeral Instances',
      'Best-effort Support',
    ],
  },
  [PlanFamily.TEAM_PLANS]: {
    name: 'Team',
    description: 'For teams',
    features: [
      'CI Performance Tier',
      'Team Usage Analytics',
      'Shared Cloud Pods',
      'Cloud Pods Storage',
      'Ephemeral Instances',
      'Prioritized Best Effort-support',
    ],
  },
  [PlanFamily.LEGACY_PLANS]: {
    name: 'Legacy',
    description: 'Legacy plan',
    features: [],
  },
  [PlanFamily.COMMUNITY]: {
    name: 'Community',
    description: 'Community plan',
    features: ['30+ AWS Services', 'Resource Browser'],
  },
};

export const SNOWFLAKE_PLAN_FAMILY_MAP: Record<KnownSnowflakePlanFamilies, PlanFamilyDescription> = {
  [PlanFamily.TRIAL_PLANS]: {
    name: 'Trial',
    description: 'Try out the power of LocalStack for Snowflake',
    features: ['Snowflake'],
    buttonText: 'Start trial',
  },
  [PlanFamily.BASE_PLANS]: {
    name: 'Base',
    description: 'Built to mitigate expensive mistakes, for any engineer.',
    features: ['Snowflake Emulator', 'Local State Persistence', 'Initialization Hooks'],
  },
  [PlanFamily.ENTERPRISE_PLANS]: {
    name: 'Enterprise',
    description: 'Customized for organizational security requirements and scale.',
    features: ['Advanced User Security', 'Advanced Emulator Compliance'],
    buttonText: 'Contact sales',
  },
};

export const AZURE_PLAN_FAMILY_MAP: Record<KnownAzurePlanFamilies, PlanFamilyDescription> = {
  [PlanFamily.PREVIEW_PLANS]: {
    name: 'Public Preview',
    description: 'Try out the power of LocalStack for Azure',
    features: AZURE_KEY_FEATURES,
    buttonText: 'Enable preview',
    tosFineprint: <StartPreviewLegalFineprint />,
  },
};

export const EMULATOR_MAP: Record<EmulatorType, Partial<Record<PlanFamily, PlanFamilyDescription>>> = {
  [EmulatorType.AWS]: AWS_PLAN_FAMILY_MAP,
  [EmulatorType.SNOWFLAKE]: SNOWFLAKE_PLAN_FAMILY_MAP,
  [EmulatorType.AZURE]: AZURE_PLAN_FAMILY_MAP,
};

/**
 * Every emulator we offer self-serve, in the order they should be presented.
 * Listed explicitly rather than derived from EMULATOR_MAP, so a product can be
 * built out before it is exposed to customers.
 */
export const EMULATORS = [EmulatorType.AWS, EmulatorType.SNOWFLAKE, EmulatorType.AZURE] as const;

/** An emulator we currently offer. Anything keyed by this must cover all of them. */
export type OfferedEmulator = (typeof EMULATORS)[number];

/**
 * An emulator whose only plan is a preview: nothing to buy, so it is offered as a preview
 * rather than as a product with plans. Goes away on its own once the product sells plans.
 */
export const isPreviewOnlyEmulator = (emulator: EmulatorType): boolean => {
  const families = Object.keys(EMULATOR_MAP[emulator]) as PlanFamily[];
  return families.length > 0 && families.every((family) => family === PlanFamily.PREVIEW_PLANS);
};
